import { FC, useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Info, Plus, Ship, Trash2 } from 'lucide-react';

import { useGetPlanning } from '@/api/generated';
import { FormSelectInput } from '@/components/form-fields/form-select-input';
import { FormTextInput } from '@/components/form-fields/form-text-input';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  excludeShipOfVisit,
  getTransferEligibleVisits,
} from '@/routes/(app)/(scenarios)/(create)/components/transshipment-transfers.utils';
import {
  CreateScenarioSchema,
  EXPLICIT_TRANSFER_CONTAINERS_MAX,
  ShipVisit,
} from '@/routes/(app)/(scenarios)/(create)/types';

const TRANSFER_ROW_CLASS = 'grid grid-cols-[1fr_1fr_9rem_2.5rem] gap-4';

const TransshipmentTransfersField: FC = () => {
  const { t } = useTranslation();
  const form = useFormContext<CreateScenarioSchema>();
  const {
    planningId,
    startTime,
    explicitTransshipmentTransfers: transfers,
  } = form.watch();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'explicitTransshipmentTransfers',
  });

  const { data: planning, isLoading } = useGetPlanning(planningId, {
    include_historic: true,
  });

  const eligibleVisits = useMemo(
    () => getTransferEligibleVisits(planning?.ship_visits ?? [], startTime),
    [planning?.ship_visits, startTime]
  );
  const toOptions = (visits: Array<ShipVisit>) =>
    visits.map((visit) => ({
      value: visit.id,
      label: t('scenarios.create.configure.transfers.visitOption', {
        visitId: visit.id,
        shipId: visit.ship_id,
      }),
    }));

  useEffect(() => {
    if (!planning) return;
    const eligibleIds = new Set(eligibleVisits.map((visit) => visit.id));
    form
      .getValues('explicitTransshipmentTransfers')
      .forEach((transfer, index) => {
        if (
          transfer.sourceShipVisitId &&
          !eligibleIds.has(transfer.sourceShipVisitId)
        ) {
          form.setValue(
            `explicitTransshipmentTransfers.${index}.sourceShipVisitId`,
            ''
          );
        }
        if (
          transfer.targetShipVisitId &&
          !eligibleIds.has(transfer.targetShipVisitId)
        ) {
          form.setValue(
            `explicitTransshipmentTransfers.${index}.targetShipVisitId`,
            ''
          );
        }
      });
  }, [planning, eligibleVisits, form]);

  const hasTransfers = fields.length > 0;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1'>
        <div className='flex items-center gap-2'>
          <h3 className='text-lg font-semibold'>
            {t('scenarios.create.configure.transfers.title')}
          </h3>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info
                className='size-4 text-muted-foreground'
                aria-label={t('scenarios.create.configure.transfers.info')}
              />
            </TooltipTrigger>
            <TooltipContent className='max-w-xs'>
              {t('scenarios.create.configure.transfers.info')}
            </TooltipContent>
          </Tooltip>
        </div>
        <p className='text-sm text-muted-foreground'>
          {t('scenarios.create.configure.transfers.description')}
        </p>
      </div>
      <div className='flex flex-col gap-3 rounded-lg border bg-muted/40 p-4'>
        {hasTransfers && (
          <div
            aria-hidden
            className={cn(TRANSFER_ROW_CLASS, 'text-sm font-medium')}>
            <span>{t('scenarios.create.configure.transfers.source')}</span>
            <span>{t('scenarios.create.configure.transfers.target')}</span>
            <span>{t('scenarios.create.configure.transfers.nContainers')}</span>
          </div>
        )}
        {fields.map((field, index) => (
          <div
            key={field.id}
            className={cn(TRANSFER_ROW_CLASS, 'items-start')}>
            <FormSelectInput
              control={form.control}
              name={`explicitTransshipmentTransfers.${index}.sourceShipVisitId`}
              options={toOptions(
                excludeShipOfVisit(
                  eligibleVisits,
                  transfers.at(index)?.targetShipVisitId ?? ''
                )
              )}
              label={t('scenarios.create.configure.transfers.source')}
              labelClassName='sr-only'
              placeholder={t(
                'scenarios.create.configure.transfers.selectVisit'
              )}
              icon={<Ship className='size-4 text-muted-foreground' />}
              isLoading={isLoading}
            />
            <FormSelectInput
              control={form.control}
              name={`explicitTransshipmentTransfers.${index}.targetShipVisitId`}
              options={toOptions(
                excludeShipOfVisit(
                  eligibleVisits,
                  transfers.at(index)?.sourceShipVisitId ?? ''
                )
              )}
              label={t('scenarios.create.configure.transfers.target')}
              labelClassName='sr-only'
              placeholder={t(
                'scenarios.create.configure.transfers.selectVisit'
              )}
              icon={<Ship className='size-4 text-muted-foreground' />}
              isLoading={isLoading}
            />
            <FormTextInput
              type='number'
              min={1}
              max={EXPLICIT_TRANSFER_CONTAINERS_MAX}
              control={form.control}
              name={`explicitTransshipmentTransfers.${index}.nContainers`}
              label={t('scenarios.create.configure.transfers.nContainers')}
              labelClassName='sr-only'
            />
            <Button
              variant='outline'
              size='icon'
              className='size-9 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive'
              aria-label={t('scenarios.create.configure.transfers.remove')}
              onClick={() => {
                remove(index);
              }}>
              <Trash2 />
            </Button>
          </div>
        ))}
        <Button
          variant='outline'
          className='w-fit border-primary text-primary hover:text-primary'
          disabled={
            isLoading ||
            new Set(eligibleVisits.map((visit) => visit.ship_id)).size < 2
          }
          onClick={() => {
            append({
              sourceShipVisitId: '',
              targetShipVisitId: '',
              nContainers: 1,
            });
          }}>
          <Plus />
          {hasTransfers
            ? t('scenarios.create.configure.transfers.addAnother')
            : t('scenarios.create.configure.transfers.add')}
        </Button>
      </div>
    </div>
  );
};

export default TransshipmentTransfersField;
